import { Component, OnInit, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import {
  ActionFailed,
  Destroyable,
  DialogService,
  Rental,
  selectAllRentals,
  selectRentalEntities,
  selectSomeOnce,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { azFilter } from '@tv3/constants/a-z-filter'
import {
  ChannelReviewItem,
  ChannelReviewSearchParams,
  channelReviewSourceOptions,
  getChannelMaxRating,
  ReviewService,
  isGuestReviewable,
} from '@tokeet-frontend/reviews'
import { FormBuilder } from '@angular/forms'
import * as lodash from 'lodash'
import { startWith, debounceTime, map, finalize, switchMap } from 'rxjs/operators'
import { ReviewAutomationsOverlayComponent } from '@tv3/containers/automations/review/automations-overlay/review-automations-overlay.component'
import { ChannelHelperService } from '@tv3/containers/channels/channel-connect/channel-helper.service'
import { OpenInquiryOverlay, OpenGuestOverlay, OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { FetchGuests } from '@tv3/store/guest/guest.actions'
import { Guest } from '@tv3/store/guest/guest.model'
import { selectGuestEntities } from '@tv3/store/guest/guest.selectors'
import { ReviewAirbnbGuestDialogService } from '../components/review-airbnb-guest-dialog/review-airbnb-guest-dialog.service'
import { selectInquiriesByIds } from '@tv3/store/inquiry/inquiry.selectors'
import { FetchInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { ReviewVrboGuestDialogService } from '../components/review-vrbo-guest-dialog/review-vrbo-guest-dialog.service'
import { of } from 'rxjs'
import { BookingRatingDialogService } from '../components/booking-rating-dialog/booking-rating-dialog.dialog.service'

@Component({
  selector: 'app-review-bookings-table',
  templateUrl: './review-bookings-table.component.html',
  styleUrls: ['./review-bookings-table.component.scss'],
})
export class ReviewBookingsTableComponent extends Destroyable implements OnInit {
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = ['reviewee.name', 'reviewerCanSubmitUntil', 'reservationId', 'source', 'edit']

  nameOptions = Object.assign([], azFilter)
  dataSource = new MatTableDataSource<ChannelReviewItem>()
  isEmpty$ = isEmptyTable(this.dataSource)
  isLoading = true

  sourceOptions = channelReviewSourceOptions.filter((o) => o.reviewable)

  filters: ChannelReviewSearchParams = {}
  totalCount = 0
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  }
  filtersForm = this.fb.group({
    sources: [[]],
    rentals: [[]],
  })

  rentals$ = this.store.pipe(select(selectAllRentals))

  expandedReviewId: string
  rentalsById: Record<string, Rental> = {}
  guestsById: Record<string, Guest> = {}

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private reviewService: ReviewService,
    private channelHelperService: ChannelHelperService,
    private dialog: DialogService,
    private bookingRatingDialog: BookingRatingDialogService,
    private reviewAirbnbGuestDialog: ReviewAirbnbGuestDialogService,
    private reviewVrboGuestDialog: ReviewVrboGuestDialogService
  ) {
    super()
  }

  ngOnInit() {
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    this.store.pipe(select(selectRentalEntities), untilDestroy(this)).subscribe((rentalsById) => {
      this.rentalsById = rentalsById
    })
    this.store.pipe(select(selectGuestEntities), untilDestroy(this)).subscribe((guestsById) => {
      this.guestsById = guestsById
    })

    this.filtersForm.valueChanges
      .pipe(
        startWith(this.filtersForm.getRawValue()),
        debounceTime(1000),
        map((filters) => {
          filters = lodash.fromPairs(
            lodash.toPairs(filters).filter(([k, v]) => !lodash.isNil(v) && (lodash.isArray(v) ? !!v.length : true))
          )

          return { ...filters }
        }),
        untilDestroy(this)
      )
      .subscribe((filters) => {
        this.filters = filters
        this.paginator.firstPage()
        this.onRefresh()
      })
  }

  onPageChange(data: PageEvent) {
    this.pagination = data
    this.onRefresh()
  }

  onRefresh() {
    this.reviewService
      .searchReviews({
        ...this.filters,
        includeStats: false,
        reviewerRole: 'host',
        reviewerCanSubmit: true,
        page: this.pagination.pageIndex + 1,
        limit: this.pagination.pageSize,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          this.dataSource.data = data.reviews
          this.totalCount = data?.counts?.total || 0
          if (data.reviews?.length) {
            const guestIds = lodash.uniq(lodash.compact(lodash.map(data.reviews, (t) => t.guestId)))
            this.store.dispatch(FetchGuests({ ids: guestIds }))
          }
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  getMaxRating(item: ChannelReviewItem) {
    return getChannelMaxRating(item.source)
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  isGuestReviewable(item: ChannelReviewItem) {
    return isGuestReviewable(item)
  }

  onReview(review: ChannelReviewItem) {
    this.store.dispatch(FetchInquiries({ ids: [review.inquiryId] }))
    this.store
      .pipe(
        selectSomeOnce(selectInquiriesByIds([review.inquiryId])),
        switchMap(([booking]) => {
          if (/airbnb/.test(booking.inquirySource)) {
            this.reviewAirbnbGuestDialog.open(booking)
          } else if (/vrbo/.test(booking.inquirySource)) {
            this.reviewVrboGuestDialog.open(booking)
          }
          return of(null)
        })
      )
      .subscribe()
  }

  onRateBooking(review: ChannelReviewItem) {
    this.store.dispatch(FetchInquiries({ ids: [review.inquiryId] }))
    this.store
      .pipe(
        selectSomeOnce(selectInquiriesByIds([review.inquiryId])),
        switchMap(([booking]) => {
          return this.bookingRatingDialog.open(booking).afterClosed()
        })
      )
      .subscribe()
  }

  onBooking(inquiryId: string) {
    this.store.dispatch(OpenInquiryOverlay({ inquiryId }))
  }

  onGuest(guestId: string) {
    this.store.dispatch(OpenGuestOverlay({ guestId }))
  }

  onRental(rentalId: string) {
    this.store.dispatch(OpenRentalOverlay({ rentalId }))
  }

  onAddChannel() {
    this.channelHelperService.connectChannel()
  }

  onAddAutomation() {
    this.dialog.openDrawer(ReviewAutomationsOverlayComponent, { panelClass: 'drawer-full-70' })
  }
}
